import { APP_BASE_HREF, registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import {
  inject,
  LOCALE_ID,
  ModuleWithProviders,
  NgModule
} from '@angular/core';
import { provideEffects } from '@ngrx/effects';
import { provideState } from '@ngrx/store';

import { provideHttpClient, withXhr } from '@angular/common/http';
import { UserConversionService } from '../services/user-conversion.service';
import { MessengerFacade } from '../components/legacy/messenger/services/messenger.facade';
import { provideCountries } from '../tokens/countries.injection-token';
import { provideApolloHttpCacheConfig } from '../tokens/apollo.injection-token';
import { provideApolloConfig } from '../providers/apollo-provider';
import { DOCUMENT_REF } from './browser/document-ref.token';
import { ScrollerService } from './browser/scroller.service';
import { WINDOW_REF } from './browser/window-ref.token';
import { ClipboardService } from './clipboard/clipboard.service';
import { CONSTANTS_LOADER } from './constants/constants-loader-service.token';
import { DownloadService } from './download/download.service';
import { DownloadUrlGeneratorService } from './download-url-generator/download-url-generator.service';
import { FileUploadService } from './file/file-upload.service';
import { DEFAULT_TRANSLATIONS_PATH } from './i18n/default-translations-path.token';
import { I18nService } from './i18n/i18n.service';
import { languages } from './i18n/languages';
import { LOCALE_CONFIG } from './i18n/locale-config.token';
import { NotificationService } from './notification/notification.service';
import { PermissionResolver } from './permission/permission.resolver';
import { PermissionService } from './permission/permission.service';
import { RoutingDetectorService } from './router/routing-detector.service';
import { SearchParamsSerializerService } from './router/search-params-serializer.service';
import { CookieService } from './storage/cookies.service';
import { InMemoryStorageService } from './storage/in-memory-storage.service';
import { LocalStorageService } from './storage/local-storage.service';
import { SessionStorageService } from './storage/session-storage.service';
import { InfrastructureConfig } from './infrastructure-config.model';
import { INFRASTRUCTURE_CONFIG } from './infrastructure-config.token';
import { reducers } from './base-state/reducers';
import { ConstantsEffects } from './base-state/constants/constants.effects';
import { NotificationEffects } from './base-state/notification/notification.effects';
import { AppEffects } from './base-state/app/app.effects';
import { MessengerEffects } from './base-state/messenger/messenger.effects';
import { DistrictEffects } from './base-state/district/district.effects';
import { RouterEffects } from './base-state/router/router.effects';
import { TranslationEffects } from './base-state/translation/translation.effects';

function getCurrentLocale(
  config: InfrastructureConfig,
  windowRef: Window = window
) {
  const availableCodes = config.languages.availableLanguagesByCode;
  const defaultCode = config.languages.defaultLanguageCode;

  const hrefLocale = windowRef.location.pathname.split('/')[1];

  return (
    availableCodes.find(code => code === hrefLocale.toString().toLowerCase()) ||
    windowRef.localStorage.getItem(config.storageKeys.localeKey) ||
    defaultCode
  );
}

function localeConfigFactory(
  config: InfrastructureConfig,
  windowRef: Window = window
) {
  return {
    localeKey: config.storageKeys.localeKey,
    currentLocale: getCurrentLocale(config, windowRef)
  };
}

function localeIdFactory(
  config: InfrastructureConfig,
  windowRef: Window = window
) {
  return getCurrentLocale(config, windowRef);
}

function baseHrefFactory(
  config: InfrastructureConfig,
  windowRef: Window = window
) {
  return '/' + getCurrentLocale(config, windowRef);
}

function defaultTranslationsPathFactory(config: InfrastructureConfig) {
  return config.defaultTranslationsPath || '/assets/i18n/translations.json';
}

function windowFactory() {
  return window;
}

function documentFactory() {
  return document;
}

@NgModule({
  providers: [
    MessengerFacade,
    provideState('infrastructure', reducers),
    provideEffects([
      RouterEffects,
      TranslationEffects,
      ConstantsEffects,
      NotificationEffects,
      AppEffects,
      MessengerEffects,
      DistrictEffects
    ])
  ]
})
export class InfrastructureModule {
  public static forRoot(
    config: InfrastructureConfig
  ): ModuleWithProviders<InfrastructureModule> {
    /**
     * The reason for using factories is that AoT compiler will complain about any
     * computations here. Even simple assignment will throw an error, therefore
     * we need to postpone creating values for those tokens and let Angular call
     * their factories.
     */
    return {
      ngModule: InfrastructureModule,
      providers: [
        { provide: INFRASTRUCTURE_CONFIG, useValue: config },
        { provide: WINDOW_REF, useFactory: windowFactory },
        {
          provide: LOCALE_CONFIG,
          useFactory: localeConfigFactory,
          deps: [INFRASTRUCTURE_CONFIG, WINDOW_REF]
        },
        {
          provide: LOCALE_ID,
          useFactory: localeIdFactory,
          deps: [INFRASTRUCTURE_CONFIG, WINDOW_REF]
        },
        {
          provide: APP_BASE_HREF,
          useFactory: baseHrefFactory,
          deps: [INFRASTRUCTURE_CONFIG, WINDOW_REF]
        },
        { provide: DOCUMENT_REF, useFactory: documentFactory },
        {
          provide: DEFAULT_TRANSLATIONS_PATH,
          useFactory: defaultTranslationsPathFactory,
          deps: [INFRASTRUCTURE_CONFIG]
        },
        provideHttpClient(withXhr()),
        provideApolloHttpCacheConfig(),
        provideApolloConfig(),
        LocalStorageService,
        SessionStorageService,
        InMemoryStorageService,
        I18nService,
        DownloadUrlGeneratorService,
        DownloadService,
        RoutingDetectorService,
        SearchParamsSerializerService,
        NotificationService,
        ClipboardService,
        FileUploadService,
        PermissionService,
        PermissionResolver,
        ScrollerService,
        CookieService,
        UserConversionService,
        provideCountries()
      ]
    };
  }

  constructor() {
    const localStorage = inject(LocalStorageService);
    const localeConfig = inject(LOCALE_CONFIG);
    const constantsLoader = inject(CONSTANTS_LOADER, { optional: true });

    if (!constantsLoader) {
      console.warn(
        'ConstantsLoader is not defined! App will not be able to load constants.'
      );
    }

    registerLocaleData(localeDe, languages.de.code);

    localStorage.setItem(localeConfig.localeKey, localeConfig.currentLocale);
  }
}
